import { useState, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { InstagramIcon, YouTubeIcon } from "./SocialIcons";

const INSTAGRAM = "https://www.instagram.com/blackmagicfilms_";
const YOUTUBE = "https://www.youtube.com/@blackmagickuper";

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [projectsDropdownOpen, setProjectsDropdownOpen] = useState(false);
  const [mobileProjectsOpen, setMobileProjectsOpen] = useState(false);
  const dropdownTimeout = useRef(null);
  const location = useLocation();

  const navItem = (isActive) =>
    `hover:text-white transition font-medium tracking-wide whitespace-nowrap ${
      isActive ? "text-white" : "text-white/60"
    }`;

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-md border-b border-white/10">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 sm:h-20 flex items-center justify-between gap-4">

        {/* Logo */}
        <Link to="/" className="group flex items-center shrink-0">
          <span className="text-base sm:text-xl md:text-2xl font-black tracking-tighter text-white uppercase italic leading-none">
            Blackmagic{" "}
            <span className="text-white/50 group-hover:text-white transition-colors">
              Films
            </span>
          </span>
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-6 text-xs uppercase tracking-[0.2em]">
          <Link to="/reel" className={navItem(location.pathname === "/reel")}>
            Reel
          </Link>

          <Link to="/thrillrot" className={navItem(location.pathname === "/thrillrot")}>
            ThrillRot EPK
          </Link>

          {/* Projects dropdown */}
          <div
            className="relative"
            onMouseEnter={() => {
              clearTimeout(dropdownTimeout.current);
              setProjectsDropdownOpen(true);
            }}
            onMouseLeave={() => {
              dropdownTimeout.current = setTimeout(() => setProjectsDropdownOpen(false), 150);
            }}
          >
            <button
              className={`flex items-center gap-1 ${navItem(location.pathname.startsWith("/mobile-production") || location.pathname === "/weddings")}`}
            >
              Projects
              <svg className="w-3 h-3 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {projectsDropdownOpen && (
              <div className="absolute top-full left-0 mt-2 w-40 bg-black border border-white/10 rounded shadow-xl z-50">
                <Link
                  to="/mobile-production"
                  className="block px-4 py-3 text-xs uppercase tracking-widest text-white/60 hover:text-white hover:bg-white/5 transition-colors"
                  onClick={() => setProjectsDropdownOpen(false)}
                >
                  Mobile Production
                </Link>
                <Link
                  to="/weddings"
                  className="block px-4 py-3 text-xs uppercase tracking-widest text-white/60 hover:text-white hover:bg-white/5 transition-colors"
                  onClick={() => setProjectsDropdownOpen(false)}
                >
                  Weddings
                </Link>
              </div>
            )}
          </div>

          <Link to="/faq" className={navItem(location.pathname === "/faq")}>
            FAQ
          </Link>

          {/* Social icons — desktop */}
          <a
            href={INSTAGRAM}
            target="_blank"
            rel="noopener noreferrer"
            className="text-white/50 hover:text-white transition-colors"
            aria-label="Instagram"
          >
            <InstagramIcon size={18} />
          </a>
          <a
            href={YOUTUBE}
            target="_blank"
            rel="noopener noreferrer"
            className="text-white/50 hover:text-white transition-colors"
            aria-label="YouTube"
          >
            <YouTubeIcon size={18} />
          </a>

          <Link
            to="/content-creation"
            className={`px-4 py-2 rounded-full border transition font-bold tracking-widest text-xs uppercase whitespace-nowrap ${
              location.pathname === "/content-creation"
                ? "bg-amber-400 text-black border-amber-400"
                : "border-amber-400 text-amber-400 hover:bg-amber-400 hover:text-black"
            }`}
          >
            Work With Me
          </Link>
        </nav>

        {/* Mobile: social icons + hamburger */}
        <div className="md:hidden flex items-center gap-3">
          <a
            href={INSTAGRAM}
            target="_blank"
            rel="noopener noreferrer"
            className="text-white/50 hover:text-white transition-colors p-1"
            aria-label="Instagram"
          >
            <InstagramIcon size={18} />
          </a>
          <a
            href={YOUTUBE}
            target="_blank"
            rel="noopener noreferrer"
            className="text-white/50 hover:text-white transition-colors p-1"
            aria-label="YouTube"
          >
            <YouTubeIcon size={18} />
          </a>

          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="text-white p-1"
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-black border-t border-white/10">
          <nav className="flex flex-col px-6 py-6 space-y-5 text-xs uppercase tracking-widest font-bold">
            <Link to="/reel" className="text-white/70 hover:text-white transition-colors" onClick={() => setMobileMenuOpen(false)}>
              Reel
            </Link>
            <Link to="/thrillrot" className="text-white/70 hover:text-white transition-colors" onClick={() => setMobileMenuOpen(false)}>
              ThrillRot EPK
            </Link>
            {/* Projects section */}
            <button
              className="text-white/70 hover:text-white transition-colors flex items-center gap-2 w-full text-left"
              onClick={() => setMobileProjectsOpen(!mobileProjectsOpen)}
            >
              Projects
              <svg className={`w-3 h-3 transition-transform ${mobileProjectsOpen ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {mobileProjectsOpen && (
              <div className="pl-4 flex flex-col space-y-4 -mt-1">
                <Link to="/mobile-production" className="text-white/50 hover:text-white transition-colors" onClick={() => setMobileMenuOpen(false)}>
                  Mobile Production
                </Link>
                <Link to="/weddings" className="text-white/50 hover:text-white transition-colors" onClick={() => setMobileMenuOpen(false)}>
                  Weddings
                </Link>
              </div>
            )}
            <Link
              to="/faq"
              className="text-white/70 hover:text-white transition-colors"
              onClick={() => setMobileMenuOpen(false)}
            >
              FAQ
            </Link>
            <Link
              to="/content-creation"
              className="text-amber-400 hover:text-amber-300 transition-colors"
              onClick={() => setMobileMenuOpen(false)}
            >
              Work With Me
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}
